import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { formatDate, downloadBlob } from '../../lib/utils';
import { FileDown, RefreshCw, BarChart3, CheckCircle } from 'lucide-react';
import { exportAssessmentsCSV } from '../../lib/api';

export default function AdminAssessments({ 
  assessments, 
  assessmentStats,
  onRefresh 
}) {
  const handleExport = async () => {
    try {
      const response = await exportAssessmentsCSV();
      downloadBlob(response.data, `assessments_export_${new Date().toISOString().split('T')[0]}.csv`);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          AI Risk Assessments
        </h2>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleExport} 
            className="rounded-none"
            data-testid="export-assessments-btn"
          >
            <FileDown className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" onClick={onRefresh} className="rounded-none">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {assessmentStats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white border border-zinc-200 p-4">
            <p className="text-sm text-zinc-500">Total</p>
            <p className="text-2xl font-bold">{assessmentStats.total_assessments}</p>
          </div>
          <div className="bg-red-50 border border-red-200 p-4">
            <p className="text-sm text-red-600">High Risk</p>
            <p className="text-2xl font-bold text-red-700">{assessmentStats.by_status?.high_risk || 0}</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 p-4">
            <p className="text-sm text-amber-600">Partial</p>
            <p className="text-2xl font-bold text-amber-700">{assessmentStats.by_status?.partial_documentation || 0}</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 p-4">
            <p className="text-sm text-emerald-600">Structured</p>
            <p className="text-2xl font-bold text-emerald-700">{assessmentStats.by_status?.structured_governance || 0}</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 p-4">
            <p className="text-sm text-blue-600">Purchased</p>
            <p className="text-2xl font-bold text-blue-700">{assessmentStats.purchased} ({assessmentStats.conversion_rate}%)</p>
          </div>
        </div>
      )}

      {assessments.length === 0 ? (
        <div className="text-center py-12 border border-zinc-200">
          <BarChart3 className="w-12 h-12 mx-auto text-zinc-300 mb-4" />
          <p className="text-zinc-500">No assessments yet</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border border-zinc-200">
            <thead className="bg-zinc-50">
              <tr>
                <th className="text-left p-3 text-sm font-medium text-zinc-600">Name</th>
                <th className="text-left p-3 text-sm font-medium text-zinc-600">Email</th>
                <th className="text-left p-3 text-sm font-medium text-zinc-600">Business</th>
                <th className="text-left p-3 text-sm font-medium text-zinc-600">Employees</th>
                <th className="text-left p-3 text-sm font-medium text-zinc-600">Score</th>
                <th className="text-left p-3 text-sm font-medium text-zinc-600">Status</th>
                <th className="text-left p-3 text-sm font-medium text-zinc-600">Purchased</th>
                <th className="text-left p-3 text-sm font-medium text-zinc-600">Date</th>
              </tr>
            </thead>
            <tbody>
              {assessments.map(assessment => (
                <tr key={assessment.id} className="border-t border-zinc-200 hover:bg-zinc-50">
                  <td className="p-3 text-sm">{assessment.name}</td>
                  <td className="p-3 text-sm text-zinc-600">{assessment.email}</td>
                  <td className="p-3 text-sm">{assessment.business_name}</td>
                  <td className="p-3 text-sm text-zinc-600">{assessment.num_employees}</td>
                  <td className="p-3 text-sm font-medium">{assessment.score}/100</td>
                  <td className="p-3">
                    <Badge 
                      variant="outline" 
                      className={`rounded-none text-xs ${
                        assessment.status === 'high_risk' 
                          ? 'bg-red-50 text-red-700 border-red-200' 
                          : assessment.status === 'partial_documentation'
                          ? 'bg-amber-50 text-amber-700 border-amber-200'
                          : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      }`}
                    >
                      {assessment.status === 'high_risk' ? 'High Risk' : 
                       assessment.status === 'partial_documentation' ? 'Partial' : 'Structured'}
                    </Badge>
                  </td>
                  <td className="p-3">
                    {assessment.purchased_pack ? (
                      <Badge variant="outline" className="rounded-none bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Yes
                      </Badge>
                    ) : (
                      <span className="text-xs text-zinc-400">No</span>
                    )}
                  </td>
                  <td className="p-3 text-sm text-zinc-500">{formatDate(assessment.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
